<script>
import gql from 'graphql-tag'
import { Notification } from '@/utils/notification'
import enums from '@/utils/enum'

export default {
  props: {
    defaultTags: {
      type: Array[String],
      default: () => [],
    },

    excludeIds: {
      type: Array,
      default: () => [],
    },

    excludeTags: {
      type: Array,
      default: () => [],
    },

    useGlobalId: {
      type: Boolean,
      default: false,
    },

    showAllUploadsByDefault: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'initial-files-loaded',
    'uploaded',
    'files-refreshed',
    'owned-tags-loaded',
    'files-deleted',
    'update-busy-status',
    'tagsAttached',
    'tagsDetached',
  ],
  data() {
    return {
      files: {}, // populated by apollo
      ownedTags: {}, // populated by apollo
      viewerFileAccess: null, // populated by apollo
      onlyMyUploadsOverride: null, // null until the user flips the toggle
      uploading: false,
      updating: false,
      deleting: false,
      recentlyUploadedFiles: [],
      hasLoadedInitialFiles: false,
    }
  },
  computed: {
    idProp() {
      return this.useGlobalId ? 'globalId' : 'localId'
    },
    isBusy() {
      return this.updating || this.uploading || this.deleting
    },
    fileData() {
      return this.files && this.files.data instanceof Array ? this.files.data : []
    },
    canViewAllFiles() {
      return this.viewerFileAccess?.canViewAll ?? true
    },
    showUploaderFilter() {
      return this.canViewAllFiles
    },
    onlyMyUploads() {
      if (!this.canViewAllFiles) {
        return true
      }
      return this.onlyMyUploadsOverride ?? !this.showAllUploadsByDefault
    },
  },
  watch: {
    isBusy(newValue) {
      this.$emit('update-busy-status', newValue)
    },
    files: {
      deep: true,
      immediate: true,
      handler() {
        this.$emit('files-refreshed', this.fileData)
      },
    },
  },
  methods: {
    setOnlyMyUploads(value) {
      this.onlyMyUploadsOverride = value
    },
    reloadFiles() {
      this.$apollo.queries.files.refetch()
    },
    reloadOwnedTags() {
      this.$apollo.queries.ownedTags.refetch()
    },
    async updateFile(file, fileName, tags, title) {
      this.updating = true
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateFile(
              $file: FileIntOrIDInput!
              $fileName: String!
              $tags: [String!]
              $title: String
            ) {
              updateFile(file: $file, fileName: $fileName, tags: $tags, title: $title) {
                globalId
                localId
                name
                extension
                storageName
                title
                humanReadableType
                humanReadableSize
                createdAt
                attachableCount
                getAttachedContractsCount
                viewerCanDelete
                tags {
                  globalId
                  localId
                  name
                }
                icon {
                  icon
                  color
                  thumbnail
                }
              }
            }
          `,
          variables: {
            file: { [this.idProp]: file },
            fileName,
            tags,
            title,
          },
        })
        .then(() => {
          this.updating = false
        })
    },
    attachTag(file, tags) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation AttachFileTags($file: FileIntOrIDInput!, $tags: [String!]) {
              attachFileTags(file: $file, tags: $tags)
            }
          `,
          variables: {
            file: { [this.idProp]: file },
            tags,
          },
        })
        .then((result) => {
          if (result.data.attachFileTags) {
            this.$emit('tagsAttached', file, tags)
          }
        })
        .catch((error) => {
          this.$emit('tagsAttachError', error)
        })
    },
    detachTag(file, tags) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation DetachFileTags($file: FileIntOrIDInput!, $tags: [String!]) {
              detachFileTags(file: $file, tags: $tags)
            }
          `,
          variables: {
            file: { [this.idProp]: file },
            tags,
          },
        })
        .then((result) => {
          if (result.data.detachFileTags) {
            this.$emit('tagsDetached', file, tags)
          }
        })
        .catch((error) => {
          this.$emit('tagsAttachError', error)
        })
    },
    async deleteFiles(ids) {
      this.deleting = true
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteFile($ids: [FileIntOrIDInput!]!) {
              deleteFiles(ids: $ids) {
                globalId
                localId
                name
                extension
                storageName
                humanReadableType
                humanReadableSize
                createdAt
                attachableCount
                getAttachedContractsCount
                viewerCanDelete
                tags {
                  globalId
                  localId
                  name
                }
                icon {
                  icon
                  color
                  thumbnail
                }
              }
            }
          `,
          variables: {
            ids: ids.map((id) => ({
              [this.idProp]: id,
            })),
          },
        })
        .then((result) => {
          if (result.hasOwnProperty('data') && result.data.hasOwnProperty('deleteFiles')) {
            this.$emit(
              'files-deleted',
              result.data.deleteFiles.map((deletedFile) => deletedFile.localId),
            )
            this.reloadFiles()
            this.reloadOwnedTags()
          }
        })
        .then(() => {
          this.deleting = false
        })
    },
    uploadFiles(files) {
      this.uploading = true
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UploadFiles($files: [Upload]!, $tags: [String!]) {
              uploadFiles(files: $files, tags: $tags) {
                globalId
                localId
                name
                extension
                storageName
                title
                humanReadableType
                humanReadableSize
                createdAt
                attachableCount
                getAttachedContractsCount
                viewerCanDelete
                tags {
                  globalId
                  localId
                  name
                }
                icon {
                  icon
                  color
                  thumbnail
                }
              }
            }
          `,
          variables: {
            files,
            tags: this.defaultTags,
          },
          context: {
            hasUpload: true, // Important!
          },
        })
        .then(
          (result) => {
            this.recentlyUploadedFiles.push(...result.data.uploadFiles)
            this.$emit('uploaded', result.data.uploadFiles)
            this.reloadFiles()
            this.reloadOwnedTags()
          },
          (error) => {
            error.graphQLErrors.forEach((gqlError) => {
              if (gqlError.extensions.category === 'validation') {
                Object.values(gqlError.extensions.validation)
                  .flat()
                  .forEach((msg) =>
                    new Notification({
                      msg,
                      type: enums.notification.types.DANGER,
                      severity: enums.notification.severity.WARNING,
                    }).dispatch(this),
                  )
              }
            })
          },
        )
        .then(() => {
          this.uploading = false
        })
    },
  },
  render() {
    return this.$slots.default({
      files: this.files,
      fileData: this.fileData,
      loading: this.$apollo?.loading || false,
      updateFile: this.updateFile,
      uploadFiles: this.uploadFiles,
      deleteFiles: this.deleteFiles,
      reloadFiles: this.reloadFiles,
      reloadOwnedTags: this.reloadOwnedTags,
      recentlyUploadedFiles: this.recentlyUploadedFiles,
      uploading: this.uploading,
      updating: this.updating,
      deleting: this.deleting,
      defaultTags: this.defaultTags,
      attachTag: this.attachTag,
      detachTag: this.detachTag,
      showUploaderFilter: this.showUploaderFilter,
      onlyMyUploads: this.onlyMyUploads,
      setOnlyMyUploads: this.setOnlyMyUploads,
    })
  },
  apollo: {
    files: {
      query: gql`
        query Files(
          $excludeIds: [FileIdInput!]
          $excludeTags: [String!]
          $uploadedByViewer: Boolean
        ) {
          files(
            first: 5000
            orderBy: [{ column: "created_at", order: DESC }]
            excludeIds: $excludeIds
            excludeTags: $excludeTags
            uploadedByViewer: $uploadedByViewer
          ) {
            data {
              globalId
              localId
              name
              extension
              storageName
              title
              humanReadableType
              humanReadableSize
              createdAt
              attachableCount
              getAttachedContractsCount
              viewerCanDelete
              deletable
              tags {
                globalId
                localId
                name
              }
              icon {
                icon
                color
                thumbnail
              }
            }
          }
        }
      `,
      variables() {
        return {
          excludeIds: this.excludeIds.map((id) => ({ [this.idProp]: id })),
          excludeTags: this.excludeTags,
          uploadedByViewer: this.onlyMyUploads,
        }
      },
      result({ data }) {
        if (data) {
          const files =
            data.hasOwnProperty('files') && data.files.hasOwnProperty('data') ? data.files.data : []

          if (!this.hasLoadedInitialFiles) {
            this.hasLoadedInitialFiles = true
            this.$emit('initial-files-loaded', files)
          }
        }
      },
    },

    viewerFileAccess: {
      query: gql`
        query ViewerFileAccess {
          viewerFileAccess {
            canViewAll
          }
        }
      `,
    },

    ownedTags: {
      query: gql`
        query OwnedTags {
          ownedTags {
            globalId
            localId
            name
          }
        }
      `,
      result({ data }) {
        const ownedTags = (data.hasOwnProperty('ownedTags') ? data.ownedTags : []).map(
          (tag) => tag.name,
        )

        this.$emit('owned-tags-loaded', ownedTags)
      },
    },
  },
}
</script>
